import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CORPORATE_SIDEBAR_PAGE_KEYS,
  DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
  getCorporatePageNavPlacement,
  type CorporatePageNavPlacement,
  type CorporateSidebarPageKey,
  type CorporateSidebarSettings,
} from '../utils/corporateSectionSettings';

interface CorporateSidebarSettingsPanelProps {
  corporateNavDisplayMode?: 'panel' | 'dropdown';
  corporateSidebarLayoutMode?: 'below_hero' | 'below_navbar';
  corporateSidebarSettings?: CorporateSidebarSettings;
  onMenuSettingChange: (setting: string, value: unknown) => void;
}

const CorporateSidebarSettingsPanel = ({
  corporateNavDisplayMode = 'dropdown',
  corporateSidebarLayoutMode = 'below_hero',
  corporateSidebarSettings = {},
  onMenuSettingChange,
}: CorporateSidebarSettingsPanelProps) => {
  const { t } = useTranslation('salesSystem');

  const sidebarSettings = {
    ...DEFAULT_CORPORATE_SIDEBAR_SETTINGS,
    ...corporateSidebarSettings,
    hideSidebarOn: {
      ...(DEFAULT_CORPORATE_SIDEBAR_SETTINGS.hideSidebarOn || {}),
      ...(corporateSidebarSettings.hideSidebarOn || {}),
    },
    pageNavPlacement: {
      ...(DEFAULT_CORPORATE_SIDEBAR_SETTINGS.pageNavPlacement || {}),
      ...(corporateSidebarSettings.pageNavPlacement || {}),
    },
  };

  const menuSnapshot = {
    corporateNavDisplayMode,
    corporateSidebarSettings: sidebarSettings,
  };

  const resolvePlacement = (page: CorporateSidebarPageKey): CorporatePageNavPlacement =>
    getCorporatePageNavPlacement(menuSnapshot, page);

  const placements = CORPORATE_SIDEBAR_PAGE_KEYS.map(resolvePlacement);
  const allNavbar = placements.every((placement) => placement === 'navbar');
  const allPanel = placements.every((placement) => placement === 'panel');
  const globalRadioValue = allNavbar ? 'dropdown' : allPanel ? 'panel' : '';
  const anyUsesSidebar = placements.some(
    (placement) => placement === 'panel' || placement === 'both'
  );

  const updateSidebarSetting = (field: keyof typeof sidebarSettings, value: string) => {
    onMenuSettingChange('corporateSidebarSettings', {
      ...sidebarSettings,
      [field]: value,
    });
  };

  const updatePageSidebarVisibility = (page: CorporateSidebarPageKey, show: boolean) => {
    onMenuSettingChange('corporateSidebarSettings', {
      ...sidebarSettings,
      hideSidebarOn: {
        ...(sidebarSettings.hideSidebarOn || {}),
        [page]: show ? undefined : true,
      },
    });
  };

  const applyPlacementToAll = (placement: CorporatePageNavPlacement) => {
    const pageNavPlacement = CORPORATE_SIDEBAR_PAGE_KEYS.reduce(
      (acc, page) => {
        acc[page] = placement;
        return acc;
      },
      {} as Record<CorporateSidebarPageKey, CorporatePageNavPlacement>
    );
    onMenuSettingChange('corporateSidebarSettings', {
      ...sidebarSettings,
      pageNavPlacement,
    });
    onMenuSettingChange(
      'corporateNavDisplayMode',
      placement === 'navbar' ? 'dropdown' : 'panel'
    );
  };

  const updatePagePlacement = (
    page: CorporateSidebarPageKey,
    placement: CorporatePageNavPlacement
  ) => {
    const pageNavPlacement = CORPORATE_SIDEBAR_PAGE_KEYS.reduce(
      (acc, key) => {
        acc[key] = key === page ? placement : resolvePlacement(key);
        return acc;
      },
      {} as Record<CorporateSidebarPageKey, CorporatePageNavPlacement>
    );
    const nextUsesSidebar = Object.values(pageNavPlacement).some(
      (value) => value === 'panel' || value === 'both'
    );
    onMenuSettingChange('corporateSidebarSettings', {
      ...sidebarSettings,
      pageNavPlacement,
    });
    onMenuSettingChange('corporateNavDisplayMode', nextUsesSidebar ? 'panel' : 'dropdown');
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
        {t('ecommerceCorporateNav.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('ecommerceCorporateNav.description')}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
              {t('ecommerceCorporateNav.displayModeTitle')}
            </FormLabel>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {t('ecommerceCorporateNav.displayModeHelp')}
            </Typography>
            <RadioGroup
              row
              value={globalRadioValue}
              onChange={(e) =>
                applyPlacementToAll(e.target.value === 'panel' ? 'panel' : 'navbar')
              }
            >
              <FormControlLabel
                value="dropdown"
                control={<Radio />}
                label={t('ecommerceCorporateNav.displayModeDropdown')}
              />
              <FormControlLabel
                value="panel"
                control={<Radio />}
                label={t('ecommerceCorporateNav.displayModePanel')}
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 1 }} />
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            {t('ecommerceCorporateNav.pagePlacementTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {t('ecommerceCorporateNav.pagePlacementHelp')}
          </Typography>
          {CORPORATE_SIDEBAR_PAGE_KEYS.map((page) => (
            <Box
              key={page}
              sx={{
                display: 'flex',
                alignItems: { xs: 'flex-start', sm: 'center' },
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                py: 1.25,
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                {t(`ecommerceCorporateNav.pages.${page}`)}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={resolvePlacement(page)}
                onChange={(_event, value: CorporatePageNavPlacement | null) => {
                  if (value) updatePagePlacement(page, value);
                }}
              >
                <ToggleButton value="navbar">
                  {t('ecommerceCorporateNav.placementNavbar')}
                </ToggleButton>
                <ToggleButton value="panel">
                  {t('ecommerceCorporateNav.placementPanel')}
                </ToggleButton>
                <ToggleButton value="both">
                  {t('ecommerceCorporateNav.placementBoth')}
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          ))}
        </Grid>

        {anyUsesSidebar && (
          <>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend" sx={{ fontSize: '0.9rem', mb: 0.5 }}>
                  {t('ecommerceMenuSettings.corporateSidebarLayout.title')}
                </FormLabel>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('ecommerceMenuSettings.corporateSidebarLayout.help')}
                </Typography>
                <RadioGroup
                  row
                  value={corporateSidebarLayoutMode || 'below_hero'}
                  onChange={(e) =>
                    onMenuSettingChange(
                      'corporateSidebarLayoutMode',
                      e.target.value === 'below_navbar' ? 'below_navbar' : 'below_hero'
                    )
                  }
                >
                  <FormControlLabel
                    value="below_hero"
                    control={<Radio />}
                    label={t('ecommerceMenuSettings.corporateSidebarLayout.belowHero')}
                  />
                  <FormControlLabel
                    value="below_navbar"
                    control={<Radio />}
                    label={t('ecommerceMenuSettings.corporateSidebarLayout.belowNavbar')}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('ecommerceCorporateNav.pageVisibilityTitle')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('ecommerceCorporateNav.pageVisibilityHelp')}
              </Typography>
              {CORPORATE_SIDEBAR_PAGE_KEYS.filter((page) => {
                const placement = resolvePlacement(page);
                return placement === 'panel' || placement === 'both';
              }).map((page) => (
                <FormControlLabel
                  key={page}
                  sx={{ display: 'flex', ml: 0, mr: 3, mb: 0.5 }}
                  control={
                    <Switch
                      checked={sidebarSettings.hideSidebarOn?.[page] !== true}
                      onChange={(e) => updatePageSidebarVisibility(page, e.target.checked)}
                    />
                  }
                  label={t(`ecommerceCorporateNav.pages.${page}`)}
                />
              ))}
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                {t('ecommerceMenuSettings.corporateSidebarColors.title')}
              </Typography>
              <Grid container spacing={2}>
                {[
                  {
                    key: 'backgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.backgroundColor'),
                  },
                  { key: 'borderColor', label: t('ecommerceMenuSettings.corporateSidebarColors.borderColor') },
                  { key: 'labelColor', label: t('ecommerceMenuSettings.corporateSidebarColors.labelColor') },
                  { key: 'itemColor', label: t('ecommerceMenuSettings.corporateSidebarColors.itemColor') },
                  {
                    key: 'itemHoverBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.itemHoverBackgroundColor'),
                  },
                  {
                    key: 'itemHoverColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.itemHoverColor'),
                  },
                  {
                    key: 'activeBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.activeBackgroundColor'),
                  },
                  {
                    key: 'activeTextColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.activeTextColor'),
                  },
                  { key: 'subitemColor', label: t('ecommerceMenuSettings.corporateSidebarColors.subitemColor') },
                  {
                    key: 'subitemActiveColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.subitemActiveColor'),
                  },
                  {
                    key: 'subitemActiveBackgroundColor',
                    label: t('ecommerceMenuSettings.corporateSidebarColors.subitemActiveBackgroundColor'),
                  },
                ].map(({ key, label }) => (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={label}
                      value={sidebarSettings[key as keyof typeof sidebarSettings] || ''}
                      onChange={(e) =>
                        updateSidebarSetting(key as keyof typeof sidebarSettings, e.target.value)
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              component="input"
                              type="color"
                              value={
                                String(sidebarSettings[key as keyof typeof sidebarSettings] || '#ffffff').startsWith(
                                  '#'
                                )
                                  ? String(sidebarSettings[key as keyof typeof sidebarSettings])
                                  : '#ffffff'
                              }
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                updateSidebarSetting(key as keyof typeof sidebarSettings, e.target.value)
                              }
                              sx={{
                                border: 'none',
                                width: 24,
                                height: 24,
                                p: 0,
                                background: 'none',
                                cursor: 'pointer',
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default CorporateSidebarSettingsPanel;
